/**
 * Shared classifier for auth/credential errors that a token refresh + retry can
 * recover from — stale Cognito Identity-Pool credentials, expired/invalid
 * tokens, "No current user" (`NoSignedUser`), etc.
 *
 * Deliberately does NOT match Cognito throttling
 * (`TooManyRequestsException` / "rate exceeded"): a throttle is not an
 * expired/invalid token, and force-refreshing in response only hammers Cognito
 * harder and self-amplifies into the credential-refresh storm (see PR #7299).
 * Throttled calls back off on their own.
 *
 * Single source of truth consumed by the GraphQL client's refresh-and-retry
 * shell, the react-query retry policy, and `withAWSRetry`.
 */
export const isAuthCredentialError = (error: unknown): error is Error => {
  if (!(error instanceof Error)) {
    return false;
  }

  const name = error.name.toLowerCase();
  const message = error.message.toLowerCase();

  // Throttle is NOT recoverable by a token refresh — never trigger one.
  if (
    name.includes("toomanyrequests") ||
    message.includes("toomanyrequests") ||
    message.includes("rate exceeded")
  ) {
    return false;
  }

  // Name-based: Cognito / STS auth exception classes.
  if (
    name.includes("nosigneduser") ||
    name.includes("notauthorized") ||
    name.includes("expiredtoken")
  ) {
    return true;
  }

  return (
    message.includes("no current user") ||
    message.includes("no signed user") ||
    message.includes("nosigneduser") ||
    message.includes("unauthorized") ||
    // Token problems — require an auth qualifier so we don't false-positive on
    // unrelated "token"/"expired" nouns ("CSRF token missing", "slot expired").
    // Still covers "token expired", "invalid login token", and AWS's
    // "The security token included in the request is expired".
    (message.includes("token") &&
      (message.includes("expired") ||
        message.includes("invalid") ||
        message.includes("revoked")))
  );
};

/**
 * Identity-Pool rejection of a token + identity-id pair that belong to different
 * users: `NotAuthorizedException: Logins don't match. Please include at least
 * one valid login for this identity or identity pool.`
 *
 * Amplify caches the signed-in identity id in each tab's memory and never
 * re-derives it from the current tokens, while the tokens themselves live in
 * localStorage shared by every tab. Any swap of the stored tokens that skips
 * Amplify's `signOut()` (impersonation start/end, signing in as another user in
 * a second tab) leaves other tabs holding the previous user's id. The login is
 * fine; only that tab's cached id is wrong.
 *
 * Not to be confused with `Access to Identity '…' is forbidden`, which is the
 * same id presented with no login at all (Amplify's guest path, i.e. no tokens).
 * Verified against the dev identity pool, 2026-10-29. Matched on message text
 * because the service uses one error name for both; if AWS rewords it, this
 * stops matching and the tab falls back to expired-session handling.
 */
export const isStaleIdentityIdError = (error: unknown): boolean =>
  error instanceof Error &&
  error.name.toLowerCase().includes("notauthorized") &&
  error.message.toLowerCase().includes("logins don't match");
